import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  buildExportTourSteps,
} from '../ui/guide';
import type {
  WorkspaceSpotlightTourStep,
} from '../../../shared/ui/common/WorkspaceSpotlightTour';
import {
  EXPORT_ONBOARDING_STORAGE_KEY,
  setDurableStateValue,
} from '../../../shared/lib/durableState/durableState';

const EXPORT_ONBOARDING_KEY = EXPORT_ONBOARDING_STORAGE_KEY;

// Writes go through the durable mirror so the "I've already seen the Export
// onboarding" flag survives browser restart / tab close and WebView2 cache
// purges; the earlier sessionStorage impl re-popped the auto-tour every fresh
// session. Reads hit localStorage, restored from the plugin mirror at
// bootstrap.
const hasSeenExportOnboarding = (): boolean => {
  try {
    return window.localStorage.getItem(EXPORT_ONBOARDING_KEY) === '1';
  } catch {
    return false;
  }
};

const markExportOnboardingSeen = (): void => {
  try {
    setDurableStateValue(EXPORT_ONBOARDING_KEY, '1');
  } catch {
    // Ignore storage failures; onboarding can reopen next visit.
  }
};

export interface ExportTourRefs {
  headerRef: React.RefObject<HTMLElement | null>;
  refreshButtonRef: React.RefObject<HTMLElement | null>;
  systemCountRef: React.RefObject<HTMLElement | null>;
  systemTableRef: React.RefObject<HTMLElement | null>;
  systemTableToolsRef: React.RefObject<HTMLElement | null>;
  systemTableRowsRef: React.RefObject<HTMLElement | null>;
  firstSystemRowRef: React.RefObject<HTMLElement | null>;
  rowHighlightButtonRef: React.RefObject<HTMLElement | null>;
  visualCheckButtonRef: React.RefObject<HTMLElement | null>;
  columnPanelRef: React.RefObject<HTMLElement | null>;
  propertySearchRef: React.RefObject<HTMLElement | null>;
  manageColumnsButtonRef: React.RefObject<HTMLElement | null>;
  exportSettingsDialogRef: React.RefObject<HTMLElement | null>;
  exportSettingsButtonRef: React.RefObject<HTMLElement | null>;
  settingsGroupingRef: React.RefObject<HTMLElement | null>;
  settingsOutputPathRef: React.RefObject<HTMLElement | null>;
  settingsValidationRef: React.RefObject<HTMLElement | null>;
  selectionSummaryRef: React.RefObject<HTMLElement | null>;
  exportActionsRef: React.RefObject<HTMLElement | null>;
  outputPathRef: React.RefObject<HTMLElement | null>;
  exportButtonRef: React.RefObject<HTMLElement | null>;
  pickAndExportButtonRef: React.RefObject<HTMLElement | null>;
}

export interface UseExportTourDeps {
  systemCount: number;
  columnPanelOpen: boolean;
  setColumnPanelOpen: (open: boolean) => void;
  settingsOpen: boolean;
  setGuideOpen: (open: boolean) => void;
  refs: ExportTourRefs;
  rememberFocusBeforeOverlay: () => void;
  restoreFocusAfterOverlay: (fallback?: HTMLElement | null) => void;
}

export interface UseExportTourResult {
  tourOpen: boolean;
  tourStep: number;
  exportTourSteps: WorkspaceSpotlightTourStep[];
  currentExportTourStepId: string | null;
  tourForcesSettingsOpen: boolean;
  effectiveSettingsOpen: boolean;
  settingsPreviewOnly: boolean;
  handleOpenTour: () => void;
  handleCloseTour: () => void;
  handleOpenGuide: () => void;
  setTourStep: (step: number) => void;
}

const SETTINGS_STEP_IDS = new Set([
  'export-settings',
  'settings-grouping',
  'settings-validation',
]);

export function useExportTour(deps: UseExportTourDeps): UseExportTourResult {
  const {
    systemCount,
    columnPanelOpen,
    setColumnPanelOpen,
    settingsOpen,
    setGuideOpen,
    refs,
    rememberFocusBeforeOverlay,
    restoreFocusAfterOverlay,
  } = deps;

  const [tourOpen, setTourOpen] = useState(false);
  const [tourStep, setTourStep] = useState(0);

  const columnPanelOpenBeforeTourRef = useRef(columnPanelOpen);
  const columnPanelManagedByTourRef = useRef(false);

  const exportTourSteps = useMemo(
    () =>
      buildExportTourSteps({
        systemCount,
        refs,
      }),
    [systemCount, refs]
  );

  const currentExportTourStepId = tourOpen
    ? exportTourSteps[tourStep]?.id ?? null
    : null;

  const tourForcesSettingsOpen =
    currentExportTourStepId !== null && SETTINGS_STEP_IDS.has(currentExportTourStepId);

  const effectiveSettingsOpen = settingsOpen || tourForcesSettingsOpen;
  const settingsPreviewOnly = tourForcesSettingsOpen && !settingsOpen;

  const closeTourState = useCallback(() => {
    setTourOpen(false);
    setTourStep(0);

    if (!columnPanelOpenBeforeTourRef.current && columnPanelManagedByTourRef.current) {
      setColumnPanelOpen(false);
    }

    columnPanelManagedByTourRef.current = false;
  }, [setColumnPanelOpen]);

  const handleCloseTour = useCallback(() => {
    closeTourState();
    restoreFocusAfterOverlay(refs.headerRef.current);
  }, [closeTourState, refs.headerRef, restoreFocusAfterOverlay]);

  const handleOpenGuide = useCallback(() => {
    rememberFocusBeforeOverlay();
    closeTourState();
    setGuideOpen(true);
  }, [closeTourState, rememberFocusBeforeOverlay, setGuideOpen]);

  const handleOpenTour = useCallback(() => {
    rememberFocusBeforeOverlay();
    columnPanelOpenBeforeTourRef.current = columnPanelOpen;
    setGuideOpen(false);
    setTourStep(0);
    setTourOpen(true);
    if (!hasSeenExportOnboarding()) {
      markExportOnboardingSeen();
    }
  }, [columnPanelOpen, rememberFocusBeforeOverlay, setGuideOpen]);

  useEffect(() => {
    if (hasSeenExportOnboarding()) {
      return;
    }

    handleOpenTour();
  }, [handleOpenTour]);

  useEffect(() => {
    if (!tourOpen) {
      return;
    }

    const currentTourStep = exportTourSteps[tourStep];
    const shouldExposeColumnPanel =
      currentTourStep?.id === 'columns' || currentTourStep?.id === 'column-search';

    if (shouldExposeColumnPanel && !columnPanelOpen) {
      columnPanelManagedByTourRef.current = true;
      setColumnPanelOpen(true);
      return;
    }

    if (
      !shouldExposeColumnPanel &&
      columnPanelManagedByTourRef.current &&
      !columnPanelOpenBeforeTourRef.current &&
      columnPanelOpen
    ) {
      setColumnPanelOpen(false);
      columnPanelManagedByTourRef.current = false;
    }
  }, [columnPanelOpen, exportTourSteps, setColumnPanelOpen, tourOpen, tourStep]);

  return {
    tourOpen,
    tourStep,
    exportTourSteps,
    currentExportTourStepId,
    tourForcesSettingsOpen,
    effectiveSettingsOpen,
    settingsPreviewOnly,
    handleOpenTour,
    handleCloseTour,
    handleOpenGuide,
    setTourStep,
  };
}
